import { useEffect, useState } from "react";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Bse2Badge } from "@/components/Bse2Badge";
import { formatCurrency, formatDate, todayDDMMYYYY } from "@/lib/format";
import { toast } from "sonner";
import { Plus, ShieldCheck, Link2, FileUp, RefreshCw } from "lucide-react";

function MandateRow({ m, onShifted }) {
  const [generating, setGenerating] = useState(false);
  const [enachUrl, setEnachUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await clientApi.post("/documents/upload", {
        flag: "MANDATE", file_name: file.name, file_bytes_b64: b64, mandate_id: m.mandate_id,
      });
      toast.success(`Scan mandate image uploaded (Ref: ${data.document_id})`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const generateEnach = async () => {
    setGenerating(true);
    try {
      const { data } = await clientApi.post(`/mandates/${m.mandate_id}/enach-url`);
      setEnachUrl(data.response_string);
      toast.success("e-NACH authentication link generated");
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setGenerating(false);
    }
  };

  const refreshStatus = async () => {
    try {
      const { data } = await clientApi.get(`/mandates/${m.mandate_id}/status`);
      toast.success(data.message || `BSE mandate status: ${data.live_status || "refreshed"}`);
      onShifted();
    } catch (error) { toast.error(getApiErrorMessage(error)); }
  };

  const maskedAccount = m.account_no ? `••••${String(m.account_no).slice(-4)}` : "not stored";

  return (
    <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`mandate-row-${m.mandate_id}`}>
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-[var(--cta)]" />
        <div>
          <div className="font-medium">Mandate #{m.mandate_id}</div>
          <div className="text-xs text-[var(--ink-muted)]">Limit {formatCurrency(m.amount)} &middot; A/c {maskedAccount} &middot; {formatDate(m.created_at)}</div>
          {enachUrl && <a href={enachUrl} target="_blank" rel="noreferrer" className="mt-1 block text-xs text-[var(--cta)] underline" data-testid={`mandate-enach-link-${m.mandate_id}`}>{enachUrl}</a>}
          {m.bse2?.auth_link && <a href={m.bse2.auth_link} target="_blank" rel="noreferrer" className="mt-1 block text-xs font-medium text-[var(--cta)] underline">Complete mandate authentication on BSE →</a>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={m.status} />
        <Bse2Badge bse2={m.bse2} />
        {m.bse2?.exch_mandate_id && <Button size="sm" variant="outline" className="rounded-full" onClick={refreshStatus}><RefreshCw className="mr-1 h-3.5 w-3.5" /> BSE status</Button>}
        {m.status !== "CANCELLED" && (
          <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:text-red-700"
            onClick={async () => {
              try {
                const { data } = await clientApi.post(`/mandates/${m.mandate_id}/cancel`);
                if (data.status === "CANCEL_RECONCILIATION_REQUIRED") toast.warning(data.bse2?.message || "BSE cancellation outcome is pending; do not repeat it.");
                else toast.success(data.bse2?.message || "Mandate cancellation submitted to BSE");
                onShifted();
              } catch (err) { toast.error(getApiErrorMessage(err)); }
            }}
            data-testid={`mandate-cancel-button-${m.mandate_id}`}>
            Cancel
          </Button>
        )}
        {m.mandate_type === "X" && m.status !== "CANCELLED" && (
            <Button size="sm" variant="outline" className="rounded-full" disabled={generating} onClick={generateEnach} data-testid={`mandate-enach-button-${m.mandate_id}`}>
              <Link2 className="mr-1 h-3.5 w-3.5" /> {generating ? "Generating..." : "e-NACH Link"}
            </Button>
        )}
        {m.mandate_type === "N" && m.status !== "CANCELLED" && (
            <label className={`flex cursor-pointer items-center gap-1 rounded-full border border-[var(--glass-border)] px-3 py-1.5 text-xs font-medium ${uploading ? "opacity-60" : ""}`} data-testid={`mandate-scan-upload-${m.mandate_id}`}>
              <FileUp className="h-3.5 w-3.5" /> {uploading ? "Uploading..." : "Upload Scan"}
              <input type="file" accept=".tiff,.tif,.jpg,.jpeg,.png,.pdf" className="hidden" onChange={uploadScan} disabled={uploading} data-testid={`mandate-scan-input-${m.mandate_id}`} />
            </label>
        )}
      </div>
    </div>
  );
}

export default function Mandates() {
  const [mandates, setMandates] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ amount: "", account_no: "", ifsc_code: "", mandate_type: "X", vpa: "" });

  const load = () => clientApi.get("/mandates").then((r) => setMandates(r.data));
  useEffect(() => { load(); }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await clientApi.post("/mandates", {
        amount: Number(form.amount), mandate_type: form.mandate_type, account_no: form.account_no,
        ifsc_code: form.ifsc_code.toUpperCase(), start_date: todayDDMMYYYY(),
        vpa: form.mandate_type === "U" ? form.vpa : undefined,
      });
      if (["SUBMITTING", "UNKNOWN"].includes(data.bse2?.status)) {
        toast.warning(data.bse2?.message || "BSE mandate confirmation is pending; do not submit it again.");
      } else {
        toast.success(data.bse2?.auth_link
          ? "Mandate submitted. Complete authentication using the BSE link in the mandate row."
          : data.bse2?.message || "Mandate accepted by BSE.");
      }
      setOpen(false);
      setForm({ amount: "", account_no: "", ifsc_code: "", mandate_type: "X", vpa: "" });
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ClientLayout>
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]">Mandates</h1>
          <p className="mt-1 text-sm text-[var(--ink-muted)]">Bank mandates authorize auto-debits for your XSIPs.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="new-mandate-button"><Plus className="mr-2 h-4 w-4" /> New Mandate</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-[var(--brand)]">Register a new mandate</DialogTitle>
              <DialogDescription>Authorizes auto-debits from your bank account for XSIP installments.</DialogDescription>
            </DialogHeader>
            <form className="space-y-4" onSubmit={handleCreate}>
              <div className="space-y-1.5"><Label>Mandate type</Label>
                <select value={form.mandate_type} onChange={update("mandate_type")} data-testid="mandate-type-select"
                  className="h-11 w-full rounded-md border border-input bg-transparent px-3 text-base sm:h-9 sm:text-sm">
                  <option value="X">e-NACH (electronic)</option>
                  <option value="N">Physical NACH (scan upload)</option>
                  <option value="U">UPI Autopay</option>
                </select>
              </div>
              {form.mandate_type === "U" && (
                <div className="space-y-1.5"><Label>UPI ID (VPA)</Label><Input required value={form.vpa} onChange={update("vpa")} placeholder="yourname@bank" data-testid="mandate-vpa-input" /></div>
              )}
              <div className="space-y-1.5"><Label>Mandate limit (INR)</Label><Input type="number" required value={form.amount} onChange={update("amount")} data-testid="mandate-amount-input" /></div>
              <div className="space-y-1.5"><Label>Bank account number</Label><Input required value={form.account_no} onChange={update("account_no")} data-testid="mandate-account-input" /></div>
              <div className="space-y-1.5"><Label>IFSC</Label><Input required maxLength={11} value={form.ifsc_code} onChange={update("ifsc_code")} data-testid="mandate-ifsc-input" /></div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[var(--cta)]" data-testid="mandate-submit-button">{loading ? "Registering..." : "Register mandate"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-[var(--glass-border)]">
        <CardContent className="p-0">
          {mandates.length === 0 ? (
            <div className="p-6 text-sm text-[var(--ink-muted)]" data-testid="no-mandates-message">No mandates registered yet.</div>
          ) : (
            <div className="divide-y divide-[var(--glass-border)]">
              {mandates.map((m) => <MandateRow key={m.mandate_id} m={m} onShifted={load} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </ClientLayout>
  );
}
